import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { S, MANROPE, FOCUS, API } from "./lib";

const LIST_FIELDS = [
  ["competitor_names", "Competitor names", "Named in an email, it blocks sending."],
  ["approved_link_domains", "Links we may send", "Any other host blocks sending."],
  ["pricing_words", "Price and discount words", "Commitments on price block sending."],
  ["delivery_words", "Promise words", "Guarantees and timeframes block sending."],
  ["banned_phrases", "Claims we do not make", "A warning, not a block."],
];

const Label = ({ children, hint }) => (
  <div className="mb-1">
    <div className="text-[9.5px] font-bold uppercase tracking-[0.1em]" style={{ color: S.ink3 }}>{children}</div>
    {hint && <div className="text-[10.5px]" style={{ color: S.muted }}>{hint}</div>}
  </div>
);

export default function OutreachSettings({ onClose }) {
  const [doc, setDoc] = useState(null);
  const [rules, setRules] = useState({});
  const [mailboxes, setMailboxes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signatures, setSignatures] = useState([]);

  useEffect(() => {
    axios.get(`${API}/signatures`).then(({ data }) => setSignatures(data.mailboxes || []))
      .catch(() => setSignatures([]));
    axios.get(`${API}/settings`).then(({ data }) => {
      setDoc(data);
      setRules(data.rules || {});
      setMailboxes(data.mailboxes || []);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Could not load the outreach settings");
      onClose();
    });
  }, [onClose]);

  if (!doc) return null;

  const setList = (key, text) =>
    setRules((r) => ({ ...r, [key]: text.split("\n").map((s) => s.trim()).filter(Boolean) }));
  const setBox = (i, key, value) =>
    setMailboxes((m) => m.map((box, idx) => (idx === i ? { ...box, [key]: value } : box)));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, { rules, mailboxes, bcc_sender: doc.bcc_sender,
        default_new_prospect_rep: doc.default_new_prospect_rep || "" });
      // The built signature is composed server-side, so re-read it for next time.
      await axios.get(`${API}/settings`);
      toast.success("Outreach settings saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="outreach-settings">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,.42)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[640px] overflow-y-auto"
        style={{ background: S.panel, borderLeft: `1px solid ${S.border}` }}>
        <div className="sticky top-0 z-10 px-5 py-3 flex items-center gap-3"
          style={{ background: S.card, borderBottom: `1px solid ${S.border}` }}>
          <div className="text-[14px] font-bold" style={{ ...MANROPE, color: S.ink }}>Outreach rules</div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={save} disabled={busy} data-testid="outreach-settings-save"
              className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase inline-flex items-center gap-1.5 disabled:opacity-50 ${FOCUS}`}
              style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000", ...MANROPE }}>
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button onClick={onClose} data-testid="outreach-settings-close"
              className={`h-9 w-9 rounded-[7px] grid place-items-center ${FOCUS}`}
              style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <X className="w-4 h-4" style={{ color: S.ink2 }} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
            <Label hint="Outreach can only go out as one of these. The signature is whatever that person has set up in Sales → Email Templates — there is nothing to keep in step here.">
              Mailboxes cleared to send
            </Label>
            {mailboxes.map((m, i) => (
              <div key={i} className="mb-3 pb-3" style={{ borderBottom: `1px solid ${S.divider}` }}>
                <div className="grid grid-cols-2 gap-2">
                  <input value={m.email || ""} onChange={(e) => setBox(i, "email", e.target.value)}
                    placeholder="address" data-testid={`outreach-mailbox-email-${i}`}
                    className={`h-9 px-2.5 rounded-[7px] text-[12.5px] mono ${FOCUS}`}
                    style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                  <input value={m.name || ""} onChange={(e) => setBox(i, "name", e.target.value)}
                    placeholder="display name" data-testid={`outreach-mailbox-name-${i}`}
                    className={`h-9 px-2.5 rounded-[7px] text-[12.5px] ${FOCUS}`}
                    style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                </div>
                {(() => {
                  const sig = (signatures || []).find((x) => x.email === m.email);
                  if (!sig) return null;
                  return (
                    <div className="mt-2" data-testid={`outreach-signature-preview-${i}`}>
                      <div className="text-[10.5px] mb-1" style={{ color: S.muted }}>
                        Signs off as <b>{sig.rep || "—"}</b>, using the signature from Sales →
                        Email Templates. {sig.set_up ? "" : "No title or phone set for them there yet."}
                      </div>
                      <div className="px-3 py-2 rounded-[7px]"
                        style={{ background: "#fff", border: `1px dashed ${S.border}` }}>
                        <div className="text-[12px]" dangerouslySetInnerHTML={{ __html: sig.html }} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
            <button onClick={() => setMailboxes((m) => [...m, { email: "", name: "", signature: "" }])}
              data-testid="outreach-mailbox-add"
              className={`text-[10.5px] font-bold uppercase ${FOCUS}`} style={{ color: S.accentInk }}>
              + Add a mailbox
            </button>
            <div className="mt-3">
              <Label hint="A prospect Claude adds from research is assigned to this person.">
                Default rep for new prospects
              </Label>
              <input value={doc.default_new_prospect_rep || ""} data-testid="outreach-default-rep"
                onChange={(e) => setDoc((d) => ({ ...d, default_new_prospect_rep: e.target.value }))}
                className={`w-full h-9 px-2.5 rounded-[7px] text-[12.5px] ${FOCUS}`}
                style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
            </div>
            <div className="mt-3 text-[11px]" data-testid="outreach-sending-note" style={{ color: S.ink3 }}>
              {doc.sending?.provider === "resend"
                ? "Sending through Resend: the sender is BCC'd, so the email reaches their Outlook inbox rather than Sent Items."
                : "Set up to send through Microsoft 365, which puts the copy in the sender's Outlook Sent Items."}
              {" "}Whether that is actually working, and the test send, live in Administration →
              Integrations → Microsoft 365.
            </div>
          </div>

          <div className="p-4 rounded-[11px] space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
            {LIST_FIELDS.map(([key, label, hint]) => (
              <div key={key}>
                <Label hint={hint}>{label}</Label>
                <textarea value={(rules[key] || []).join("\n")} onChange={(e) => setList(key, e.target.value)}
                  rows={Math.min(8, Math.max(3, (rules[key] || []).length + 1))}
                  data-testid={`outreach-rule-${key}`}
                  className={`w-full px-2.5 py-2 rounded-[7px] text-[12px] ${FOCUS}`}
                  style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                <div className="text-[10px]" style={{ color: S.muted }}>One per line</div>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-[11px] space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
            <div>
              <Label hint="Every outreach email must contain a way out. This line is what the + button inserts.">
                Opt-out line
              </Label>
              <textarea value={rules.optout_line || ""} rows={2} data-testid="outreach-rule-optout"
                onChange={(e) => setRules((r) => ({ ...r, optout_line: e.target.value }))}
                className={`w-full px-2.5 py-2 rounded-[7px] text-[12px] ${FOCUS}`}
                style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[["max_subject_chars", "Subject limit"], ["max_body_words", "Word limit"],
                ["repeat_touch_window_days", "Repeat window (days)"]].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <input type="number" value={rules[key] ?? ""} data-testid={`outreach-rule-${key}`}
                    onChange={(e) => setRules((r) => ({ ...r, [key]: Number(e.target.value) }))}
                    className={`w-full h-9 px-2.5 rounded-[7px] text-[12.5px] mono ${FOCUS}`}
                    style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
